// HU-01: completar el registro con nombre, celular y documento. También sirve para editar el perfil.

import { useState, type FormEvent } from "react";
import { Navigate, useSearchParams } from "react-router";
import { normalizarDocumento, TIPOS_DOCUMENTO, validarDocumento, type TipoDocumento, type Usuario } from "@porteria/core";
import { llamar } from "../firebase";
import { rutaDeVuelta, useSesion } from "../sesion";
import { Boton, Campo, Cargando, Pantalla, Tarjeta, useAccion } from "../ui";

export function FormularioPerfil(props: {
  perfil?: Usuario | null;
  nombreSugerido?: string | null;
  celularSugerido?: string | null;
  textoBoton: string;
  alGuardar?: () => void;
}) {
  const { ejecutar, ocupado } = useAccion();
  const [nombre, setNombre] = useState(props.perfil?.nombre ?? props.nombreSugerido ?? "");
  // Quien entró con su celular ya lo escribió: se muestra sin el +57.
  const [celular, setCelular] = useState(props.perfil?.celular ?? props.celularSugerido?.replace(/^\+57/, "") ?? "");
  const [tipo, setTipo] = useState<TipoDocumento>(props.perfil?.tipoDocumento ?? "CC");
  const [numero, setNumero] = useState("");
  const [acepta, setAcepta] = useState(!!props.perfil);
  const [tocado, setTocado] = useState(false);

  const errorDoc = numero ? validarDocumento(tipo, numero) : tocado ? "Escribe el número de documento." : null;

  const enviar = async (e: FormEvent) => {
    e.preventDefault();
    setTocado(true);
    if (validarDocumento(tipo, numero) || nombre.trim().length < 3 || !acepta) return;
    const r = await ejecutar(
      "guardar",
      () => llamar("completarRegistro", { nombre: nombre.trim(), celular, tipoDocumento: tipo, numeroDocumento: normalizarDocumento(numero), aceptaDatos: acepta }),
      props.perfil ? "Datos actualizados" : undefined,
    );
    if (r) props.alGuardar?.();
  };

  return (
    <form className="pila" onSubmit={enviar}>
      <Campo etiqueta="Nombre completo" error={tocado && nombre.trim().length < 3 ? "Escribe tu nombre." : null}>
        <input autoComplete="name" value={nombre} onChange={(e) => setNombre(e.target.value)} maxLength={80} />
      </Campo>
      <Campo etiqueta="Celular" ayuda="La administración lo usa si necesita contactarte. El domiciliario y la portería no lo ven.">
        <input type="tel" inputMode="tel" autoComplete="tel" value={celular} onChange={(e) => setCelular(e.target.value)} maxLength={20} />
      </Campo>
      <Campo etiqueta="Tipo de documento">
        <select value={tipo} onChange={(e) => setTipo(e.target.value as TipoDocumento)}>
          {TIPOS_DOCUMENTO.map((t) => (
            <option key={t.valor} value={t.valor}>
              {t.etiqueta}
            </option>
          ))}
        </select>
      </Campo>
      <Campo
        etiqueta="Número de documento"
        error={errorDoc}
        ayuda={props.perfil ? `Actual: •••• ${props.perfil.documentoUltimos4}. Escríbelo completo para confirmar.` : "La administración lo usa para verificar que vives en el conjunto."}
      >
        <input inputMode={tipo === "CC" ? "numeric" : "text"} autoComplete="off" value={numero} onChange={(e) => setNumero(e.target.value)} maxLength={20} />
      </Campo>
      {!props.perfil && (
        <label className="casilla chico">
          <input type="checkbox" checked={acepta} onChange={(e) => setAcepta(e.target.checked)} />
          <span>
            Autorizo el tratamiento de mis datos personales (nombre, documento, celular y apartamento) para prestar el servicio de domicilio
            interno, según la Ley 1581 de 2012. Solo la administración de mi conjunto ve mi documento completo.
          </span>
        </label>
      )}
      {tocado && !acepta && <p className="campo-error">Para usar el servicio debes aceptar el tratamiento de datos.</p>}
      <Boton type="submit" grande ocupado={ocupado === "guardar"}>
        {props.textoBoton}
      </Boton>
    </form>
  );
}

export default function Registro() {
  const { cuenta, perfil, salir } = useSesion();
  const [parametros] = useSearchParams();
  const [enviado, setEnviado] = useState(false);
  const { ejecutar, ocupado } = useAccion();
  if (cuenta === undefined || (cuenta && perfil === undefined)) return <Cargando />;
  if (!cuenta) return <Navigate to="/entrar" replace />;
  // Con el registro completo sigue a donde iba (p. ej. /unirme?codigo=… del QR del afiche).
  if (perfil) return <Navigate to={rutaDeVuelta(parametros)} replace />;
  if (enviado) return <Cargando texto="Guardando tus datos…" />;

  return (
    <Pantalla titulo="Completa tu registro" subtitulo={cuenta.email ?? cuenta.phoneNumber ?? undefined}>
      <Tarjeta>
        <FormularioPerfil nombreSugerido={cuenta.displayName} celularSugerido={cuenta.phoneNumber} textoBoton="Continuar" alGuardar={() => setEnviado(true)} />
      </Tarjeta>
      <Boton type="button" variante="texto" ocupado={ocupado === "salir"} onClick={() => ejecutar("salir", salir)}>
        Salir y entrar con otra cuenta
      </Boton>
    </Pantalla>
  );
}
